import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Check, Loader2, Plus } from 'lucide-react';
import { DECLINE_REASONS, APPROVAL_LEVELS } from './types';

interface WizardModalsProps {
  showDeclineModal: boolean;
  setShowDeclineModal: (v: boolean) => void;
  declineReason: string;
  setDeclineReason: (v: string) => void;
  declineNotes: string;
  setDeclineNotes: (v: string) => void;
  handleDecline: () => void;
  saving: boolean;

  showApproveModal: boolean;
  setShowApproveModal: (v: boolean) => void;
  approvalLevel: string;
  setApprovalLevel: (v: string) => void;
  handleApprove: () => void;

  showCreateGroupModal: boolean;
  setShowCreateGroupModal: (v: boolean) => void;
  newGroupName: string;
  setNewGroupName: (v: string) => void;
  handleCreateGroup: () => void;
}

/**
 * Modal dialogs used in the Supplier Onboarding Wizard:
 * - Decline Modal: reason select + notes textarea
 * - Approve Modal: approval level select
 * - Create Group Modal: new group name input
 */
export default function WizardModals({
  showDeclineModal,
  setShowDeclineModal,
  declineReason,
  setDeclineReason,
  declineNotes,
  setDeclineNotes,
  handleDecline,
  saving,
  showApproveModal,
  setShowApproveModal,
  approvalLevel,
  setApprovalLevel,
  handleApprove,
  showCreateGroupModal,
  setShowCreateGroupModal,
  newGroupName,
  setNewGroupName,
  handleCreateGroup,
}: WizardModalsProps) {
  return (
    <>
      {/* Decline Modal */}
      <Dialog open={showDeclineModal} onOpenChange={setShowDeclineModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline Supplier</DialogTitle>
            <DialogDescription>
              Please provide a reason for declining this supplier.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reason</Label>
              <Select value={declineReason} onValueChange={setDeclineReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason..." />
                </SelectTrigger>
                <SelectContent>
                  {DECLINE_REASONS.map((reason) => (
                    <SelectItem key={reason} value={reason}>
                      {reason}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Additional Notes</Label>
              <Textarea
                value={declineNotes}
                onChange={(e) => setDeclineNotes(e.target.value)}
                placeholder="Enter additional notes..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeclineModal(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDecline} disabled={!declineReason || saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Decline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Modal */}
      <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Supplier</DialogTitle>
            <DialogDescription>
              Select the approval level for this supplier.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Approval Level</Label>
              <Select value={approvalLevel} onValueChange={setApprovalLevel}>
                <SelectTrigger>
                  <SelectValue placeholder="Select approval level..." />
                </SelectTrigger>
                <SelectContent>
                  {APPROVAL_LEVELS.map((level) => (
                    <SelectItem key={level.id} value={level.id.toString()}>
                      {level.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleApprove} disabled={!approvalLevel || saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Check className="h-4 w-4 mr-2" />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CR-3942: Create New Group Modal */}
      <Dialog open={showCreateGroupModal} onOpenChange={setShowCreateGroupModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Supplier Group</DialogTitle>
            <DialogDescription>
              Enter the name for the new supplier group. Format: Company name – group
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new_group_name">Group Name <span className="text-red-500">*</span></Label>
              <Input
                id="new_group_name"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="e.g. Global Division – Manufacturing"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateGroupModal(false); setNewGroupName(''); }}>
              Cancel
            </Button>
            <Button onClick={handleCreateGroup} disabled={!newGroupName.trim()}>
              <Plus className="h-4 w-4 mr-2" />
              Create Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
